import React, { useState, useEffect } from 'react';
import { Switch } from '@headlessui/react';
import { BeakerIcon, ServerIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { useDataSource } from '../../contexts/DataSourceContext';

export const DataSourceToggle: React.FC = () => {
  const { mode, setMode, isDemoMode } = useDataSource();

  const handleToggle = (checked: boolean) => {
    setMode(checked ? 'demo' : 'live');
    console.log(`Switched to ${checked ? 'Demo' : 'Live'} Mode`);
  };

  return (
    <div className="flex items-center gap-3 px-4 py-2 bg-white rounded-lg border border-neutral-200 shadow-sm">
      {/* Live Mode Icon */}
      <div className="flex items-center gap-2">
        <ServerIcon
          className={`h-5 w-5 transition-colors ${
            !isDemoMode ? 'text-green-600' : 'text-neutral-300'
          }`}
        />
        <span
          className={`text-sm font-medium transition-colors ${
            !isDemoMode ? 'text-green-700' : 'text-neutral-400'
          }`}
        >
          Live
        </span>
      </div>

      {/* Toggle Switch */}
      <Switch
        checked={isDemoMode}
        onChange={handleToggle}
        className={`${
          isDemoMode ? 'bg-amber-500' : 'bg-green-500'
        } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
          isDemoMode ? 'focus:ring-amber-500' : 'focus:ring-green-500'
        }`}
      >
        <span className="sr-only">Toggle data source</span>
        <motion.span
          layout
          className={`${
            isDemoMode ? 'translate-x-6' : 'translate-x-1'
          } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
        />
      </Switch>

      {/* Demo Mode Icon */}
      <div className="flex items-center gap-2">
        <span
          className={`text-sm font-medium transition-colors ${
            isDemoMode ? 'text-amber-700' : 'text-neutral-400'
          }`}
        >
          Demo
        </span>
        <BeakerIcon
          className={`h-5 w-5 transition-colors ${
            isDemoMode ? 'text-amber-600' : 'text-neutral-300'
          }`}
        />
      </div>

      {/* Mode Indicator Badge */}
      <AnimatePresence mode="wait">
        <motion.div
          key={isDemoMode ? 'demo' : 'live'}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          className={`ml-2 px-2 py-1 rounded-md text-xs font-semibold ${
            isDemoMode
              ? 'bg-amber-100 text-amber-800'
              : 'bg-green-100 text-green-800'
          }`}
        >
          {isDemoMode ? 'Sample Data' : 'Real Data'}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// Banner component to show when in demo mode
export const DemoModeBanner: React.FC = () => {
  const { isDemoMode } = useDataSource();

  if (!isDemoMode) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 rounded-lg shadow-sm"
    >
      <div className="flex items-center gap-3">
        <BeakerIcon className="h-6 w-6 text-amber-600 flex-shrink-0" />
        <div>
          <h3 className="text-sm font-semibold text-amber-900">
            Demo Mode Active
          </h3>
          <p className="text-xs text-amber-700 mt-0.5">
            You're viewing sample data for demonstration purposes. Switch to Live Mode to see real system data.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

